import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { tagsPropTypes } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { Card, CardTitle } from 'components/Card';
import Message from 'components/Message';
import messages, { notificationTypeMessages } from './Notifications.messages';
import {
  NOTIFICATION_CATEGORY,
  NOTIFICATION_CATEGORY_LIST,
  NOTIFICATION_CATEGORY_TYPES,
  NOTIFICATION_TYPE_LIST,
} from './Notifications.constants';
import VenueDropdown, { ALL_VENUES_VALUE } from './VenueDropdown';

const { element } = bem('notifications');

const categoryTitles = {
  [NOTIFICATION_CATEGORY.OPERATIONAL]: messages.categoryOperational,
  [NOTIFICATION_CATEGORY.PERFORMANCE]: messages.categoryPerformance,
};

const isTypeChecked = ({ currentValues, scope, applicableIds }) =>
  scope === ALL_VENUES_VALUE
    ? applicableIds.length > 0 && applicableIds.every(id => currentValues.includes(id))
    : currentValues.includes(scope);

const nextTypeValues = ({ currentValues, scope, applicableIds, checked }) => {
  if (scope === ALL_VENUES_VALUE) {
    return checked
      ? Array.from(new Set([...currentValues, ...applicableIds]))
      : currentValues.filter(id => !applicableIds.includes(id));
  }
  return checked ? [...currentValues, scope] : currentValues.filter(id => id !== scope);
};

const VenueEmailCheckbox = ({ type, scope, applicableIds }) => {
  const { watch, setValue } = useFormContext();
  const name = `receiveNotifications.${type}`;
  const currentValues = watch(name) || [];
  const isChecked = isTypeChecked({ currentValues, scope, applicableIds });

  const handleChange = ({ target: { checked } }) => {
    setValue(name, nextTypeValues({ currentValues, scope, applicableIds, checked }), { shouldDirty: true });
  };

  return (
    <label className={element('email-row')}>
      <span className={element('email-row-text')}>
        <Message as="span" className={element('email-row-title')} message={notificationTypeMessages[type].title} />
        <Message
          as="span"
          className={element('email-row-description')}
          message={notificationTypeMessages[type].description}
        />
      </span>
      <input
        type="checkbox"
        id={name}
        className={element('email-checkbox')}
        checked={isChecked}
        onChange={handleChange}
      />
    </label>
  );
};

VenueEmailCheckbox.propTypes = {
  type: PropTypes.oneOf(NOTIFICATION_TYPE_LIST),
  scope: PropTypes.string,
  applicableIds: PropTypes.arrayOf(PropTypes.string),
};

const SelectAllCheckbox = ({ types, scope, tagTrees }) => {
  const intl = useIntl();
  const { watch, setValue } = useFormContext();
  const receiveNotifications = watch('receiveNotifications') || {};
  const applicableIdsFor = type => tagTrees[type].map(({ id }) => id);

  const allChecked = types.every(type =>
    isTypeChecked({ currentValues: receiveNotifications[type] || [], scope, applicableIds: applicableIdsFor(type) })
  );

  const handleChange = ({ target: { checked } }) => {
    types.forEach(type => {
      const currentValues = receiveNotifications[type] || [];
      const values = nextTypeValues({ currentValues, scope, applicableIds: applicableIdsFor(type), checked });
      setValue(`receiveNotifications.${type}`, values, { shouldDirty: true });
    });
  };

  return (
    <input
      type="checkbox"
      className={element('email-checkbox')}
      checked={allChecked}
      onChange={handleChange}
      aria-label={intl.formatMessage(messages.venueEmailsSelectAll)}
    />
  );
};

SelectAllCheckbox.propTypes = {
  types: PropTypes.arrayOf(PropTypes.oneOf(NOTIFICATION_TYPE_LIST)),
  scope: PropTypes.string,
  tagTrees: PropTypes.shape(NOTIFICATION_TYPE_LIST.reduce((memo, type) => ({ ...memo, [type]: tagsPropTypes }), {})),
};

const VenueEmailsSection = ({ venueOptions, emailTypes, tagTrees }) => {
  const isSingleVenue = venueOptions.length === 1;
  const [scope, setScope] = useState(isSingleVenue ? venueOptions[0].id : ALL_VENUES_VALUE);

  const categories = NOTIFICATION_CATEGORY_LIST.map(category => ({
    category,
    types: NOTIFICATION_CATEGORY_TYPES[category].filter(type => {
      if (!emailTypes.includes(type)) return false;
      if (scope === ALL_VENUES_VALUE) return tagTrees[type].length > 0;
      return tagTrees[type].some(({ id }) => id === scope);
    }),
  })).filter(({ types }) => types.length > 0);

  const visibleTypes = categories.flatMap(({ types }) => types);

  return (
    <Card className={element('card')}>
      <Message as={CardTitle} message={messages.venueEmailsTitle} />
      {!isSingleVenue && (
        <div className="mv-s">
          <VenueDropdown options={venueOptions} selectedId={scope} onSelect={setScope} />
        </div>
      )}
      {categories.map(({ category, types }, index) => (
        <div key={category} className={element('venue-email-category')}>
          <div className={element('venue-email-category-header')}>
            <Message as="h3" className={element('venue-email-category-title')} message={categoryTitles[category]} />
            {index === 0 && <SelectAllCheckbox types={visibleTypes} scope={scope} tagTrees={tagTrees} />}
          </div>
          {types.map(type => (
            <VenueEmailCheckbox
              key={type}
              type={type}
              scope={scope}
              applicableIds={tagTrees[type].map(({ id }) => id)}
            />
          ))}
        </div>
      ))}
    </Card>
  );
};

VenueEmailsSection.propTypes = {
  venueOptions: tagsPropTypes,
  emailTypes: PropTypes.arrayOf(PropTypes.oneOf(NOTIFICATION_TYPE_LIST)),
  tagTrees: PropTypes.shape(NOTIFICATION_TYPE_LIST.reduce((memo, type) => ({ ...memo, [type]: tagsPropTypes }), {})),
};

export default VenueEmailsSection;
